/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * PreferenceSettingStyles
 *
 * @file PreferenceSettingStyles.jsx
 * @author Anuja Gaikwad
 * @since 1.0.27
 */
import { styled } from '@mui/material/styles';
import Paper from '@mui/material/Paper';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import mixins from '../../../assets/styles/mixins.scss';

export const PreferenceDialogPaper = styled(Paper)({
  '&&': {
    width: '584px',
    maxWidth: '800px',
    borderRadius: '24px',
    padding: '48px',
    gap: 24,
    maxHeight: '767px'
  }
});

export const PreferenceTitleRoot = styled(DialogTitle)({
  '&&': {
    padding: 0,
    margin: 0
  }
});

export const PreferenceContentRoot = styled(DialogContent)({
  '&&': {
    padding: 0,
    margin: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    gap: 16
  }
});

export const PreferenceActionsRoot = styled(DialogActions)({
  '&&': {
    padding: 0,
    margin: 0
  }
});

export const PreferenceTitle = styled('div')({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize24,
    fontWeight: mixins.fontWeight500,
    lineHeight: '32px',
    letterSpacing: '-0.48px'
  }
});

export const PreferenceSubtitle = styled('div')({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight400,
    lineHeight: mixins.lineHeight24,
    marginTop: 16
  }
});

export const PreferenceSectionsWrapper = styled('div')({
  '&&': {
    display: 'flex',
    flexDirection: 'column',
    gap: 12
  }
});

export const PreferenceSectionCard = styled('div')({
  '&&': {
    background: mixins.feedBackColor,
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column'
  }
});

export const PreferenceMainRow = styled('div')({
  '&&': {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'nowrap',
    marginBottom: 5
  }
});

export const PreferenceLabel = styled('div')({
  '&&': {
    color: mixins.color_text_primary,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: mixins.fontSize16,
    fontWeight: mixins.fontWeight500,
    lineHeight: mixins.lineHeight20
  }
});

export const PreferenceSubLabel = styled('div')({
  '&&': {
    color: mixins.zambezi,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '12px',
    lineHeight: '14px',
    maxWidth: '460px',
    display: 'flex',
    flexDirection: 'column',
    gap: 5,
    fontWeight: mixins.fontWeight500,
    '& b': {
      fontWeight: mixins.fontWeight600
    }
  }
});

export const PreferenceLearnMore = styled('a')({
  '&&': {
    color: mixins.amethyst,
    fontFamily: mixins.fontFamilyPlusSans,
    fontSize: '12px',
    fontWeight: mixins.fontWeight600,
    lineHeight: '14px',
    cursor: 'pointer',
    marginTop: 12,
    '&:hover': {
      textDecoration: 'underline'
    }
  }
});

export const PreferenceCloseButton = styled(IconButton)({
  '&&': {
    position: 'absolute',
    right: 16,
    top: 16
  }
});

export const PreferenceFooter = styled('div')({
  '&&': {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end'
  }
});

export const PreferenceConfirmButton = styled('button')({
  '&&': {
    background: mixins.amethyst,
    fontFamily: mixins.fontFamilyPlusSans,
    color: mixins.chalkWhiteFont,
    borderRadius: '1000px',
    fontSize: mixins.fontSize14,
    padding: '8px 16px',
    fontWeight: mixins.fontWeight500,
    height: '34px',
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    lineHeight: '16px',
    border: 'none',
    outline: 'none',
    transition: 'background-color 0.15s ease-in-out',

    '&:hover': {
      backgroundColor: mixins.amethystColorV2
    },

    '&:disabled': {
      backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
      color: mixins.bulkEditCtaButtonDisabledColor,
      cursor: 'not-allowed'
    }
  }
});

export const PreferenceStudyPrepDivider = styled('div')({
  '&&': {
    width: '100%',
    height: '1px',
    backgroundColor: mixins.bulkEditCtaButtonDisabledBGColor,
    marginTop: '12px',
    marginBottom: '12px'
  }
});
